<template>
  <modal class="download-dates-modal" :is-opened="isOpened" :on-close="onClose">
    <template #header>
      <div class="download-dates-modal__header">
        {{ trans('organization.adaf.dates_modal.title') }}
      </div>
    </template>
    <template #body>
      <div class="modal-state">
        <label>{{ trans('organization.adaf.dates_modal.start_label') }}</label>
        <div class="modal-state__start-date">
          <div class="input-select" :class="{ error: v$.startMonth.$error }">
            <select v-model="startMonth" @change="onChange">
              <option disabled value="">
                {{ trans('global.month') }}
              </option>
              <option
                v-for="option in monthOptions"
                :key="option.id"
                :value="option.id"
              >
                {{ option.name }}
              </option>
            </select>
            <p v-show="v$.startMonth.$error" class="error">
              {{ trans('organization.adaf.dates_modal.required') }}
            </p>
          </div>
          <div class="input-select" :class="{ error: v$.startYear.$error }">
            <select v-model="startYear" @change="onChange">
              <option disabled value="">
                {{ trans('global.year') }}
              </option>
              <option
                v-for="option in yearOptions"
                :key="option.id"
                :value="option.id"
              >
                {{ option.name }}
              </option>
            </select>
            <p v-show="v$.startYear.$error" class="error">
              {{ trans('organization.adaf.dates_modal.required') }}
            </p>
          </div>
        </div>
        <label>{{ trans('organization.adaf.dates_modal.end_label') }}</label>
        <div class="modal-state__end-date">
          <div class="input-select" :class="{ error: v$.endMonth.$error }">
            <select v-model="endMonth" @change="onChange">
              <option disabled value="">
                {{ trans('global.month') }}
              </option>
              <option
                v-for="option in monthOptions"
                :key="option.id"
                :value="option.id"
              >
                {{ option.name }}
              </option>
            </select>
            <p v-show="v$.endMonth.$error" class="error">
              {{ trans('organization.adaf.dates_modal.required') }}
            </p>
          </div>
          <div class="input-select" :class="{ error: v$.endYear.$error }">
            <select v-model="endYear" @change="onChange">
              <option disabled value="">
                {{ trans('global.year') }}
              </option>
              <option
                v-for="option in yearOptions"
                :key="option.id"
                :value="option.id"
              >
                {{ option.name }}
              </option>
            </select>
            <p v-show="v$.endYear.$error" class="error">
              {{ trans('organization.adaf.dates_modal.required') }}
            </p>
          </div>
        </div>
        <div v-show="datesAreInvalid" class="error">
          {{ trans('organization.adaf.dates_modal.error') }}
        </div>
      </div>
    </template>
    <template #footer>
      <div>
        <div class="modal-state__actions">
          <button class="btn btn--secondary" @click="onClose">
            {{ trans('global.cancel') }}
          </button>
          <button class="btn btn--primary" :class="{ disabled: v$.$error }" @click="onSubmit">
            {{ trans('global.validate') }}
          </button>
        </div>
      </div>
    </template>
  </modal>
</template>

<script>
import { ref, computed } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import moment from 'moment';
import Modal from '@components/Modal.vue';
import translation, { trans } from '@elements/translation';

export default {
  name: 'AdafDownloadCustomDatesModal',
  components: { Modal },
  mixins: [ translation ],
  props: {
    isOpened: { type: Boolean, default: true },
  },
  setup(props, { emit }) {
    const startMonth = ref('');
    const startYear = ref('');
    const endMonth = ref('');
    const endYear = ref('');
    const datesAreInvalid = ref(false);

    const v$ = useVuelidate({
      startMonth: { required },
      startYear: { required },
      endMonth: { required },
      endYear: { required },
    }, { startMonth, startYear, endMonth, endYear });

    const monthOptions = computed(() => [
      { id: 1, name: trans('global.january') },
      { id: 2, name: trans('global.february') },
      { id: 3, name: trans('global.march') },
      { id: 4, name: trans('global.april') },
      { id: 5, name: trans('global.may') },
      { id: 6, name: trans('global.june') },
      { id: 7, name: trans('global.july') },
      { id: 8, name: trans('global.august') },
      { id: 9, name: trans('global.september') },
      { id: 10, name: trans('global.october') },
      { id: 11, name: trans('global.november') },
      { id: 12, name: trans('global.december') },
    ]);

    const yearOptions = computed(() => {
      const currentYear = moment().year();
      const firstAllowedYear = 2012;
      return Array.from({ length: currentYear - (firstAllowedYear - 1) }, (v, k) => ({ id: firstAllowedYear + k, name: String(firstAllowedYear + k) })).reverse();
    });

    function onChange() {
      datesAreInvalid.value = false;
    }

    function onClose() {
      startMonth.value = '';
      startYear.value = '';
      endMonth.value = '';
      endYear.value = '';
      datesAreInvalid.value = false;
      emit('close');
      v$.value.$reset();
    }

    function onSubmit() {
      v$.value.$touch();

      if (v$.value.$error) {
        return;
      }

      const startMonthToSubmit = moment();
      startMonthToSubmit.year(this.startYear);
      startMonthToSubmit.month(this.startMonth - 1);

      const endMonthToSubmit = moment();
      endMonthToSubmit.year(this.endYear);
      endMonthToSubmit.month(this.endMonth - 1);

      if (startMonthToSubmit.isAfter(endMonth, 'month')) {
        this.datesAreInvalid = true;
        return;
      }

      emit('onselectdates', { startMonthToSubmit, endMonthToSubmit });
      onClose();
    }

    return {
      startMonth,
      startYear,
      endMonth,
      endYear,
      datesAreInvalid,
      monthOptions,
      yearOptions,
      onChange,
      onClose,
      onSubmit,
      v$,
    };
  },
};
</script>
